import { IPagination, IPaginationRequest } from "interfaces/pagination";
import { useCallback, useEffect, useState } from "react";
import WarrantyServices from "services/taylormadeWarranty";
import Header from "components/common/Header";
import Table from "components/common/Table";
import dayjs from "dayjs";
import useAlert from "hooks/useAlert";
import useToken from "hooks/useToken";
import withAuth from "hocs/withAuth";
import withPermission from "hocs/withPermission";
import { useNavigate } from "react-router";
import { Dialog, DialogContent } from "@mui/material";

interface IWarrantyData {
  id: number;
  category: string;
  serialnumber: string;
  dealer: string;
  purchasedate: string;
  warrantyexpire: string;
  receiptUrl: string;
  createdat: string;
  updatedat: string;
}

interface IPaginationTaylorMade {
  page: number;
  total: number;
  totalPages: number;
}

const WarrantyReport = () => {
  const navigate = useNavigate();
  const { token } = useToken();
  const { onAlert } = useAlert();
  const [data, setData] = useState<{
    data: IWarrantyData[];
    pagination: IPagination;
  }>({
    data: [],
    pagination: {
      page: 1,
      total_item: 0,
      total_page: 0,
    },
  });

  const [pagination, setPagination] = useState<IPaginationRequest>({
    page: 1,
    per_page: 10,
  });
  const [search, setSearch] = useState<string>("");
  const [filter, setFilter] = useState<{
    date: {
      start: Date | null;
      end: Date | null;
    };
    expire_date: {
      start: Date | null;
      end: Date | null;
    };
  }>({
    date: { start: null, end: null },
    expire_date: { start: null, end: null },
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = useCallback(() => {
    setIsLoading(true);

    WarrantyServices.getAllWarrantyReport(
      token as string,
      {
        per_page: pagination.per_page,
        page: pagination.page,
      },
      search,
      {
        start: filter.date.start
          ? dayjs(filter.date.start.toString()).format("YYYY-MM-DD")
          : "",
        end: filter.date.end
          ? dayjs(filter.date.end.toString()).format("YYYY-MM-DD")
          : "",
      },
      {
        start: filter.expire_date.start
          ? dayjs(filter.expire_date.start.toString()).format("YYYY-MM-DD")
          : "",
        end: filter.expire_date.end
          ? dayjs(filter.expire_date.end.toString()).format("YYYY-MM-DD")
          : "",
      },
    )
      .then((res) => {
        let paginationRes: IPaginationTaylorMade = res.data.meta;
        setData({
          data: res.data.data.map((d: IWarrantyData) => {
            return {
              id: d.id,
              category: d.category,
              serialnumber: d.serialnumber,
              dealer: d.dealer,
              purchasedate: d.purchasedate,
              warrantyexpire: d.warrantyexpire,
              receiptUrl: d.receiptUrl,
              createdat: d.createdat,
              updatedat: d.updatedat,
            } as IWarrantyData;
          }),
          pagination: {
            page: paginationRes.page,
            total_item: paginationRes.total,
            total_page: paginationRes.totalPages,
          },
        });
        setIsLoading(false);
      })
      .catch((err) => {
        onAlert("error", err.response.data.message);
        setIsLoading(false);
      });
  }, [token, onAlert, pagination, search, filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onExport = useCallback(() => {
    WarrantyServices.exportWarrantyReport(
      token as string,
      search,
      {
        start: filter.date.start
          ? dayjs(filter.date.start.toString()).format("YYYY-MM-DD")
          : "",
        end: filter.date.end
          ? dayjs(filter.date.end.toString()).format("YYYY-MM-DD")
          : "",
      },
      {
        start: filter.expire_date.start
          ? dayjs(filter.expire_date.start.toString()).format("YYYY-MM-DD")
          : "",
        end: filter.expire_date.end
          ? dayjs(filter.expire_date.end.toString()).format("YYYY-MM-DD")
          : "",
      },
    )
      .then((res) => {
        // window.location.href = process.env.REACT_APP_TAYLORMADE + "/warranties/export";
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link: any = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Warranty_Report_${dayjs().format("YYYYMMDDHHmm")}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      })
      .catch((err) => {
        onAlert("error", err.response.data.message);
      });
  }, [token, search, filter, onAlert]);

  return (
    <>
      <Header
        title="Warranty Report"
        breadcrumbs={[
          {
            label: "Warranty Report",
            href: "/taylormade/warranty-report",
          },
          {
            label: "List",
          },
        ]}
      />

      <Table
        headers={[
          {
            key: "id",
            label: "ID",
          },
          {
            key: "category",
            label: "Category",
          },
          {
            key: "serialnumber",
            label: "Serial Number",
          },
          {
            key: "dealer",
            label: "Dealer",
          },
          {
            key: "purchasedate",
            label: "Purchase Date",
            render: (value) => {
              return dayjs(value).format("DD/MM/YYYY");
            },
          },
          {
            key: "warrantyexpire",
            label: "Expire Date",
            render: (value) => {
              return dayjs(value).format("DD/MM/YYYY");
            },
          },
          {
            key: "receiptUrl",
            label: "Receipt",
            render: (value) => {
              return value ? <ReceiptImage value={value} /> : null;
              // return value ? (
              //   <img
              //     src={value}
              //     alt="receipt"
              //     style={{
              //       width: 70,
              //       height: 70,
              //       objectFit: "cover",
              //       cursor: "pointer",
              //       borderRadius: 8,
              //     }}
              //     onClick={() => window.open(value, "_blank")}
              //   />
              // ) : null;
            },
          },
          {
            key: "createdat",
            label: "Create Date",
            render: (value) => {
              return dayjs(value).format("DD/MM/YYYY");
            },
          },
          {
            key: "updatedat",
            label: "Update Date",
            render: (value) => {
              return dayjs(value).format("DD/MM/YYYY");
            },
          },
        ]}
        data={data.data}
        isLoading={isLoading}
        onSearch={(searchText) => setSearch(searchText)}
        onExport={onExport}
        filters={[
          {
            type: "daterange",
            name: "Purchase date",
            value: filter.date,
            onChange: (value: any) =>
              setFilter((filter) => {
                return { ...filter, date: value };
              }),
          },
          {
            type: "daterange",
            name: "Expire date",
            value: filter.expire_date,
            onChange: (value: any) =>
              setFilter((filter) => {
                return { ...filter, expire_date: value };
              }),
          },
        ]}
        pagination={
          data.pagination
            ? {
                ...data.pagination,
                onChange: (page) =>
                  setPagination((pagination) => {
                    return {
                      ...pagination,
                      page,
                    };
                  }),
              }
            : undefined
        }
        actions={{
          onEdit: (item: any) =>
            navigate(`/taylormade/warranty-report/${item.id}/edit`),
        }}
      />
    </>
  );
};

const ReceiptImage = ({ value }: { value: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <img
        src={value}
        alt="receipt"
        style={{
          width: 70,
          height: 70,
          objectFit: "cover",
          cursor: "pointer",
          borderRadius: 8,
        }}
        onClick={() => setOpen(true)}
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 0,
            backgroundColor: "transparent",
          }}
        >
          <img
            onClick={() => setOpen(false)}
            src={value}
            alt="receipt-preview"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: 12,
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default withAuth(
  withPermission(WarrantyReport, "MANAGE_TAYLORMADE_WARRANTY"),
);
